import { useState, useEffect, useCallback } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Zap, ArrowLeft, Check, X as XIcon, Package, Truck } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const STATUS_COLORS = {
  pending: "text-yellow-400",
  confirmed: "text-[#39FF14]",
  shipped: "text-cyan-400",
  fulfilled: "text-blue-400",
  cancelled: "text-red-400",
};

export default function AdminPreordersPage() {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [preorders, setPreorders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(null);
  const [trackingInputs, setTrackingInputs] = useState({});

  const fetchPreorders = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/admin/preorders`);
      setPreorders(data);
    } catch (e) {
      console.error("Failed to load preorders", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading) return;
    if (!user || user.role !== "admin") { navigate("/"); return; }
    fetchPreorders();
  }, [user, authLoading, navigate, fetchPreorders]);

  const handleUpdateStatus = async (preorderId, newStatus, tracking = null) => {
    setUpdating(preorderId);
    try {
      const body = {};
      if (newStatus) body.status = newStatus;
      if (tracking) body.tracking_number = tracking;
      await axios.put(`${API}/api/admin/preorders/${preorderId}/status`, body);
      fetchPreorders();
      setTrackingInputs(prev => { const n = {...prev}; delete n[preorderId]; return n; });
    } catch (e) {
      console.error("Update failed", e);
    } finally {
      setUpdating(null);
    }
  };

  // Group by product
  const grouped = preorders.reduce((acc, p) => {
    if (!acc[p.product_id]) acc[p.product_id] = { product_name: p.product_name, product_id: p.product_id, entries: [] };
    acc[p.product_id].entries.push(p);
    return acc;
  }, {});

  if (authLoading || loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: "#050505" }}>
        <div className="w-8 h-8 border border-[#39FF14] border-t-transparent animate-spin" />
      </div>
    );
  }

  const activeCount = preorders.filter(p => ["confirmed", "pending"].includes(p.status)).length;

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#050505" }}>
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-8">
          <div>
            <p className="font-mono uppercase tracking-[0.3em] text-xs font-bold text-[#39FF14] mb-2">Admin</p>
            <h1 data-testid="admin-preorders-heading" className="text-4xl sm:text-5xl font-black uppercase tracking-tighter text-[#F2F2F2]" style={{ fontFamily: "Chivo, sans-serif" }}>
              Preorder Manager
            </h1>
          </div>
          <Button onClick={() => navigate("/admin")} variant="outline" className="bg-transparent border-[#1A1A1A] hover:border-[#39FF14] text-[#A1A1A1] hover:text-[#39FF14] rounded-none font-mono text-xs uppercase tracking-wider" data-testid="back-to-admin-btn">
            <ArrowLeft className="h-4 w-4 mr-2" strokeWidth={1.5} />Products
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-[1px] bg-[#1A1A1A] mb-8">
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Total</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]">{preorders.length}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Active</p>
            <p className="font-mono text-2xl font-bold text-[#39FF14]">{activeCount}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Fulfilled</p>
            <p className="font-mono text-2xl font-bold text-blue-400">{preorders.filter(p => p.status === "fulfilled").length}</p>
          </div>
          <div className="bg-[#0D0D0D] p-4 sm:p-6">
            <p className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#A1A1A1] mb-1">Cancelled</p>
            <p className="font-mono text-2xl font-bold text-red-400">{preorders.filter(p => p.status === "cancelled").length}</p>
          </div>
        </div>

        {Object.keys(grouped).length === 0 ? (
          <div className="bg-[#0D0D0D] border border-[#1A1A1A] p-12 text-center" data-testid="no-preorders">
            <Zap className="h-12 w-12 text-[#1A1A1A] mx-auto mb-4" />
            <p className="text-[#A1A1A1] text-lg">No preorders yet.</p>
          </div>
        ) : (
          <div className="space-y-6">
            {Object.values(grouped).map((group) => (
              <div key={group.product_id} className="border border-[#1A1A1A]" data-testid={`preorder-group-${group.product_id}`}>
                <div className="bg-[#0D0D0D] p-4 border-b border-[#1A1A1A] flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Zap className="h-4 w-4 text-[#39FF14]" />
                    <Link to={`/products/${group.product_id}`} className="text-[#F2F2F2] font-bold text-sm hover:text-[#39FF14] transition-colors uppercase tracking-tight" style={{ fontFamily: "Chivo, sans-serif" }}>{group.product_name}</Link>
                  </div>
                  <span className="font-mono text-xs text-[#39FF14]">{group.entries.filter(e => ["confirmed","pending"].includes(e.status)).length} active</span>
                </div>

                <div className="bg-[#0D0D0D]">
                  <div className="hidden sm:grid grid-cols-[60px_1fr_1fr_60px_100px_100px_160px] gap-3 p-3 border-b border-[#1A1A1A]">
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">#</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Name</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Email</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Qty</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Amount</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Status</span>
                    <span className="font-mono uppercase tracking-[0.2em] text-[10px] font-bold text-[#39FF14]">Date</span>
                  </div>
                  {group.entries.map((entry, idx) => (
                    <div key={entry.id} data-testid={`preorder-entry-${entry.id}`} className="p-3 border-b border-[#1A1A1A]/50 hover:bg-[#141414] transition-colors">
                      <div className="flex flex-col sm:grid sm:grid-cols-[60px_1fr_1fr_60px_100px_100px_160px] gap-2 sm:gap-3 items-center">
                        <span className="font-mono text-sm text-[#39FF14] font-bold">{idx + 1}</span>
                        <span className="text-[#F2F2F2] text-sm">{entry.user_name}</span>
                        <span className="text-[#A1A1A1] text-sm truncate">{entry.user_email}</span>
                        <span className="font-mono text-sm text-[#F2F2F2]">{entry.quantity || 1}</span>
                        <span className="font-mono text-sm text-[#39FF14]">${entry.amount?.toFixed(2)}</span>
                        <span className={`font-mono text-xs uppercase ${STATUS_COLORS[entry.status] || "text-[#A1A1A1]"}`}>{entry.status}</span>
                        <span className="font-mono text-xs text-[#A1A1A1]">{new Date(entry.created_at).toLocaleString()}</span>
                      </div>
                      {/* Tracking + Actions row */}
                      <div className="flex flex-wrap items-center gap-2 mt-2 pl-0 sm:pl-[60px]">
                        {entry.tracking_number && (
                          <span className="font-mono text-xs text-cyan-400"><Truck className="h-3 w-3 inline mr-1" />{entry.tracking_number}</span>
                        )}
                        {["confirmed", "shipped"].includes(entry.status) && (
                          <div className="flex items-center gap-1">
                            <input
                              placeholder={entry.tracking_number || "Tracking #"}
                              value={trackingInputs[entry.id] || ""}
                              onChange={(e) => setTrackingInputs(prev => ({ ...prev, [entry.id]: e.target.value }))}
                              className="bg-[#050505] border border-[#1A1A1A] text-[#F2F2F2] px-2 py-1 text-xs font-mono w-36 focus:border-cyan-400 focus:outline-none"
                              data-testid={`tracking-input-${entry.id}`}
                            />
                            <button onClick={() => handleUpdateStatus(entry.id, "shipped", trackingInputs[entry.id])} disabled={updating === entry.id || !trackingInputs[entry.id]} data-testid={`ship-${entry.id}`} className="px-2 py-1 border border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 font-mono text-[10px] uppercase tracking-wider disabled:opacity-50">
                              <Truck className="h-3 w-3 inline mr-1" />Ship
                            </button>
                          </div>
                        )}
                        {["confirmed", "shipped"].includes(entry.status) && (
                          <button onClick={() => handleUpdateStatus(entry.id, "fulfilled")} disabled={updating === entry.id} data-testid={`fulfill-${entry.id}`} className="px-2 py-1 border border-blue-500/50 text-blue-400 hover:bg-blue-500/10 font-mono text-[10px] uppercase tracking-wider disabled:opacity-50">
                            <Package className="h-3 w-3 inline mr-1" />Fulfill
                          </button>
                        )}
                        {["confirmed", "pending", "shipped"].includes(entry.status) && (
                          <button onClick={() => handleUpdateStatus(entry.id, "cancelled")} disabled={updating === entry.id} data-testid={`cancel-preorder-${entry.id}`} className="px-2 py-1 border border-red-500/50 text-red-400 hover:bg-red-500/10 font-mono text-[10px] uppercase tracking-wider disabled:opacity-50">
                            <XIcon className="h-3 w-3 inline mr-1" />Cancel
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
